import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2, Lock, Plus, Tag, Trash2, UserRoundPen, X } from 'lucide-react';
import {
  useChargeCostDefinitions,
  useCustomerRateCard,
  useOrderChargeCosts,
  useSetChargeBillTo,
  type ChargeCostDefinition,
  type CustomerAdditionalCharge,
} from '@my/tms-api';
import { ConfirmDialog, ErrorBanner, Loading, showToast } from '@my/ui';
import {
  formatAmount,
  newCustomLine,
  lineFromDto,
  isServerId,
  sumNet,
  type ChargeCostLine,
} from '../lib/charges';
import { applyCustomerRates, buildChargeLine, recomputeLocked } from '../lib/rateEngine';
import { quoteTransport, type TransportJob, type TransportQuote } from '../lib/transportPricing';

type PricedQuote = Extract<TransportQuote, { kind: 'priced' }>;

/** Current editable state lifted to the page, which persists it on the order's footer Save. */
export interface ChargeCostSnapshot {
  charges: ChargeCostLine[];
  costs: ChargeCostLine[];
  /** True once the panel has hydrated — the page skips the charge save until then. */
  ready: boolean;
}

interface ChargesAndCostsPanelProps {
  /** The order these lines belong to; null on a not-yet-saved order. */
  orderId: string | null;
  /** Org id — for the charge-definition catalogue + customer rate card. */
  organisationId: string | null;
  /** Selected customer — for the rate card (fuel surcharge + additionals). */
  customerPartyId: string | null;
  /** Delivery country ISO (best-effort) — drives fuel-surcharge territory match. */
  deliveryCountryIso: string | null;
  /** Order/org currency (ISO). Both sides are valued in this. */
  currency: string;
  /**
   * The job as pricing sees it — cargo type, tasks, quantities. Null until
   * there's something to price (a new order, or no cargo picked yet).
   */
  transportJob: TransportJob | null;
  /** Reports the current lines up so the order's footer Save can persist them. */
  onChange: (snapshot: ChargeCostSnapshot) => void;
  /**
   * Who the order's charges go to by default (its invoice-to, else the
   * customer) and the parties a single charge can be billed to instead.
   */
  orderPayerPartyId?: string | null;
  payers?: { value: string; label: string }[];
}

/** The Charges side's "bill this one to someone else" wiring. */
interface BillToProps {
  orderPayerPartyId: string | null;
  payers: { value: string; label: string }[];
  pendingId: string | null;
  onBillTo: (lineId: string, partyId: string | null) => void;
}

/**
 * Order → Charges & Costs. Two halves split by a divider: Costs (purchase) on
 * the LEFT, Charges (sales, feeds invoicing) on the RIGHT.
 *
 * The Charges side is rate-aware: order creation seeds only the Transport
 * Charge, and this panel applies the selected customer's rate card —
 * auto-adding a locked **Fuel Surcharge** line (rate% of the FS-applicable
 * base, recomputed live) and offering the org charge catalogue under + Add
 * (applying the customer's additional rate per type). Costs stay a plain NET
 * ledger. Edits are local until Save, which atomically replaces the whole set.
 */
export function ChargesAndCostsPanel({
  orderId,
  organisationId,
  customerPartyId,
  deliveryCountryIso,
  currency,
  transportJob,
  onChange,
  orderPayerPartyId = null,
  payers = [],
}: ChargesAndCostsPanelProps) {
  const linesQuery = useOrderChargeCosts(orderId, { refetchOnWindowFocus: false });
  const rateCardQuery = useCustomerRateCard(organisationId, customerPartyId, {
    refetchOnWindowFocus: false,
  });
  const defsQuery = useChargeCostDefinitions(organisationId, { refetchOnWindowFocus: false });

  const [charges, setCharges] = useState<ChargeCostLine[]>([]);
  const [costs, setCosts] = useState<ChargeCostLine[]>([]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Hydrate the editable copy from the server. Re-runs only when a fresh fetch
  // arrives — tracked by the query's `dataUpdatedAt` — so local edits aren't
  // clobbered between saves, but a save's refetch (server ids + locked
  // snapshots) flows back in.
  const hydratedStamp = useRef(-1);
  useEffect(() => {
    if (!orderId || !linesQuery.isSuccess) return;
    if (linesQuery.dataUpdatedAt === hydratedStamp.current) return;
    hydratedStamp.current = linesQuery.dataUpdatedAt;

    const persisted = linesQuery.data ?? [];
    setCharges(persisted.filter((r) => r.side === 'Charge').map(lineFromDto));
    setCosts(persisted.filter((r) => r.side === 'Cost').map(lineFromDto));
  }, [orderId, linesQuery.isSuccess, linesQuery.dataUpdatedAt, linesQuery.data]);

  // Apply the customer's rate card to the Charges: mark Transport FS-applicable,
  // ensure/sync the Fuel Surcharge line, recompute locked lines. Runs once per
  // (customer / rate-card / definitions / hydration) change — guarded so it
  // doesn't fight ongoing edits — and uses a functional update so it augments
  // whatever's current rather than rebuilding from the DB.
  const appliedKey = useRef('');
  useEffect(() => {
    if (!rateCardQuery.isSuccess || !defsQuery.isSuccess) return;
    const ready = !orderId || linesQuery.isSuccess;
    if (!ready) return;
    const key = [
      customerPartyId ?? '',
      deliveryCountryIso ?? '',
      rateCardQuery.dataUpdatedAt,
      defsQuery.dataUpdatedAt,
      linesQuery.dataUpdatedAt,
    ].join('|');
    if (appliedKey.current === key) return;
    appliedKey.current = key;

    const rateCard = rateCardQuery.data;
    const definitions = defsQuery.data ?? [];
    setCharges((prev) =>
      applyCustomerRates(prev, {
        definitions,
        fuelSurcharges: rateCard?.fuelSurcharges ?? [],
        deliveryCountryIso,
        today,
        seedTransportIfMissing: !orderId,
      }),
    );
  }, [
    orderId,
    customerPartyId,
    deliveryCountryIso,
    today,
    linesQuery.isSuccess,
    linesQuery.dataUpdatedAt,
    rateCardQuery.isSuccess,
    rateCardQuery.dataUpdatedAt,
    rateCardQuery.data,
    defsQuery.isSuccess,
    defsQuery.dataUpdatedAt,
    defsQuery.data,
  ]);

  const ready = !orderId || linesQuery.isSuccess;
  const loadError = linesQuery.isError;

  // Billing one charge to someone else saves straight away (its own endpoint);
  // the local copy is patched so the operator's unsaved edits stay put.
  const billTo = useSetChargeBillTo(organisationId);
  const [billToPending, setBillToPending] = useState<string | null>(null);
  const setLineBillTo = (lineId: string, partyId: string | null) => {
    setBillToPending(lineId);
    billTo.mutate(
      { chargeCostId: lineId, partyId },
      {
        onSuccess: (saved) =>
          setCharges((prev) =>
            prev.map((l) => (l.id === lineId ? { ...l, billToPartyId: saved.billToPartyId ?? null } : l)),
          ),
        onError: (err) =>
          showToast({ type: 'error', message: err.message || 'Could not change who pays this charge.' }),
        onSettled: () => setBillToPending(null),
      },
    );
  };

  // Report the current lines up so the order's footer Save persists them
  // alongside the order. The page holds these in a ref (no re-render).
  useEffect(() => {
    onChange({ charges, costs, ready });
  }, [charges, costs, ready, onChange]);

  // + Add catalogue for the Charges side: every active definition the operator
  // can add on demand, minus the auto-managed Transport / Fuel Surcharge and
  // anything already on the order. Each carries the customer's additional rate.
  const additionalsByDefId = useMemo(() => {
    const map = new Map<string, CustomerAdditionalCharge>();
    for (const a of rateCardQuery.data?.additionals ?? []) map.set(a.chargeCostDefinitionId, a);
    return map;
  }, [rateCardQuery.data]);

  const pickableDefinitions = useMemo(() => {
    const used = new Set(charges.map((l) => l.definitionId).filter(Boolean) as string[]);
    return (defsQuery.data ?? []).filter(
      (d) =>
        d.status === 'Active' &&
        d.type !== 'TransportCharge' &&
        d.type !== 'FuelSurcharge' &&
        !used.has(d.id),
    );
  }, [defsQuery.data, charges]);

  // Charges edits go through recomputeLocked so the fuel surcharge (and any
  // %-of-base line) tracks the FS-applicable base as the operator types.
  const updateCharges = (next: ChargeCostLine[]) => setCharges(recomputeLocked(next));

  // ── Transport price from the customer's rate card ─────────────────────────
  // What the rate/route sheets say this job costs. Recomputed whenever the job
  // changes (a task added, a pallet count edited) — the page rebuilds
  // `transportJob` from the refetched tasks.
  const quote = useMemo<TransportQuote>(
    () =>
      transportJob && rateCardQuery.data
        ? quoteTransport(rateCardQuery.data, transportJob, today)
        : { kind: 'none' },
    [transportJob, rateCardQuery.data, today],
  );

  const transportLine = charges.find((l) => l.type === 'TransportCharge') ?? null;
  const persistedTransportNet =
    linesQuery.data?.find((l) => l.side === 'Charge' && l.type === 'TransportCharge')?.net ?? null;

  // The fuel surcharge is a % of the Transport Charge, so it follows.
  const applyTransportPrice = useCallback((net: number) => {
    setCharges((prev) =>
      recomputeLocked(prev.map((l) => (l.type === 'TransportCharge' ? { ...l, net } : l))),
    );
  }, []);

  /**
   * An open question to the operator about the transport price:
   *   apply   — the charge is empty, but this sheet is set to ask first.
   *   reprice — there's already a price, and the job has since changed so the
   *             rate card now gives a different one.
   */
  const [prompt, setPrompt] = useState<{
    kind: 'apply' | 'reprice';
    quote: PricedQuote;
    current: number;
  } | null>(null);

  // Read inside the effect below without making every keystroke re-run it.
  const chargesRef = useRef(charges);
  chargesRef.current = charges;
  // The quote this session last acted on, and the ones the operator turned down
  // — both per ORDER: moving to another order without leaving the page must
  // start fresh, or its opening price would look like a change to this one.
  const actedQuoteKey = useRef<string | null>(null);
  const declinedQuoteKeys = useRef(new Set<string>());
  const quoteMemoryOrderId = useRef(orderId);
  if (quoteMemoryOrderId.current !== orderId) {
    quoteMemoryOrderId.current = orderId;
    actedQuoteKey.current = null;
    declinedQuoteKeys.current = new Set();
  }

  const quoteKey = quote.kind === 'priced' ? `${quote.sheetId}|${quote.net}` : quote.kind;
  const hasTransportLine = transportLine !== null;

  // Decide what a NEW quote means for the order. Keyed on the quote itself, so
  // it runs when the job changes the price — not on every edit to the lines.
  //
  //   • Charge still empty (€0): the rate card fills it — straight away when
  //     the sheet's AutoApply is on, otherwise by asking first.
  //   • Charge already set and the job changed since the page opened: ask
  //     whether to re-price. A typed or agreed price is never overwritten
  //     without a yes.
  //   • Charge set and different when the page first OPENS: no popup. That's
  //     most often a deliberate special price, and asking on every visit would
  //     nag; the inline notice still offers the re-price.
  useEffect(() => {
    // Nothing is judged until the whole job is here. Marking a quote as "seen"
    // while the job was still loading would make the real one look like a
    // change, and ask to re-price an order the moment it opens.
    if (!ready || !rateCardQuery.isSuccess || !hasTransportLine || !transportJob) return;
    if (quote.kind !== 'priced') {
      actedQuoteKey.current = quoteKey;
      return;
    }
    if (actedQuoteKey.current === quoteKey) return;
    const openedWithThisQuote = actedQuoteKey.current === null;
    actedQuoteKey.current = quoteKey;

    const current =
      chargesRef.current.find((l) => l.type === 'TransportCharge')?.net ?? 0;
    if (current === quote.net || declinedQuoteKeys.current.has(quoteKey)) return;

    if (current === 0) {
      if (quote.autoApply) applyTransportPrice(quote.net);
      else setPrompt({ kind: 'apply', quote, current });
      return;
    }
    if (!openedWithThisQuote) setPrompt({ kind: 'reprice', quote, current });
  }, [
    ready,
    rateCardQuery.isSuccess,
    hasTransportLine,
    transportJob,
    quote,
    quoteKey,
    applyTransportPrice,
  ]);

  const closePrompt = (accepted: boolean) => {
    if (!prompt) return;
    if (accepted) applyTransportPrice(prompt.quote.net);
    else declinedQuoteKeys.current.add(`${prompt.quote.sheetId}|${prompt.quote.net}`);
    setPrompt(null);
  };

  const transportNotice =
    transportLine && quote.kind !== 'none' ? (
      <TransportRateNotice
        quote={quote}
        currentNet={transportLine.net}
        persistedNet={persistedTransportNet}
        currency={currency}
        onApply={
          quote.kind === 'priced' ? () => applyTransportPrice(quote.net) : undefined
        }
      />
    ) : null;

  return (
    <section className="rounded-md border border-slate-200 bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-2">
        <div className="flex items-baseline gap-2">
          <h2 className="text-sm font-semibold text-slate-900">Charges &amp; Costs</h2>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {currency}
          </span>
          {!orderId && (
            <span className="text-[10px] italic text-slate-400">
              Preview — save the order first to keep these.
            </span>
          )}
        </div>
      </header>

      {!ready && !loadError && (
        <div className="px-3 py-2">
          <Loading label="Loading charges & costs" />
        </div>
      )}
      {loadError && (
        <div className="px-3 py-2">
          <ErrorBanner
            title="Failed to load charges & costs"
            message={linesQuery.error?.message ?? 'Unknown error'}
          />
        </div>
      )}

      {/* Two halves divided by a grey vertical line: Costs (left) | Charges (right). */}
      {ready && !loadError && (
        <div className="grid grid-cols-1 divide-y divide-slate-200 md:grid-cols-2 md:divide-x md:divide-y-0">
          <LedgerColumn title="Costs" lines={costs} currency={currency} onChange={setCosts} />
          <LedgerColumn
            title="Charges"
            lines={charges}
            currency={currency}
            onChange={updateCharges}
            pickerDefinitions={pickableDefinitions}
            additionalsByDefId={additionalsByDefId}
            notice={transportNotice}
            billTo={
              orderId
                ? { orderPayerPartyId, payers, pendingId: billToPending, onBillTo: setLineBillTo }
                : undefined
            }
          />
        </div>
      )}

      <ConfirmDialog
        open={prompt !== null}
        title={
          prompt?.kind === 'reprice'
            ? 'Re-price the transport charge?'
            : 'Apply the customer’s transport price?'
        }
        message={
          prompt && (
            <div className="space-y-2 text-sm text-slate-600">
              {prompt.kind === 'reprice' && (
                <p>The job has changed, and the rate card now gives a different price.</p>
              )}
              <p>
                <span className="font-medium text-slate-800">{prompt.quote.source}</span>
                <br />
                {prompt.quote.detail} ={' '}
                <span className="font-semibold text-slate-800">
                  {formatAmount(prompt.quote.net, currency)}
                </span>
              </p>
              {prompt.kind === 'reprice' && (
                <p>Transport charge now: {formatAmount(prompt.current, currency)}</p>
              )}
            </div>
          )
        }
        confirmLabel={prompt ? `Apply ${formatAmount(prompt.quote.net, currency)}` : 'Apply'}
        cancelLabel={
          prompt?.kind === 'reprice' ? `Keep ${formatAmount(prompt.current, currency)}` : 'Not now'
        }
        onConfirm={() => closePrompt(true)}
        onCancel={() => closePrompt(false)}
      />
    </section>
  );
}

/**
 * One line under the Charges heading saying what the customer's rate card
 * makes of this job — so a price that appeared by itself is explained, and a
 * price that differs from the rate card can be brought back into line.
 */
function TransportRateNotice({
  quote,
  currentNet,
  persistedNet,
  currency,
  onApply,
}: {
  quote: Exclude<TransportQuote, { kind: 'none' }>;
  currentNet: number;
  persistedNet: number | null;
  currency: string;
  onApply?: () => void;
}) {
  if (quote.kind === 'unpriced') {
    return (
      <p className="mb-2 flex items-start gap-1.5 rounded-sm bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
        <Tag className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
        <span>Rate card: {quote.reason}</span>
      </p>
    );
  }

  const matches = currentNet === quote.net;
  return (
    <div
      className={
        'mb-2 flex items-start justify-between gap-2 rounded-sm px-2 py-1 text-[11px] ' +
        (matches ? 'bg-slate-50 text-slate-500' : 'bg-amber-50 text-amber-800')
      }
    >
      <p className="flex items-start gap-1.5">
        <Tag className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
        <span>
          {matches ? 'Priced from the rate card' : `Rate card gives ${formatAmount(quote.net, currency)}`}
          {' — '}
          {quote.source}: {quote.detail}
          {/* The panel's edits are local until the order's Save. */}
          {matches && persistedNet !== quote.net && (
            <span className="font-medium text-slate-700"> Save the order to keep it.</span>
          )}
        </span>
      </p>
      {!matches && onApply && (
        <button
          type="button"
          onClick={onApply}
          className="shrink-0 rounded-sm px-1.5 py-0.5 font-medium text-purple-600 hover:bg-purple-50"
        >
          {currentNet === 0 ? 'Apply' : 'Re-price'}
        </button>
      )}
    </div>
  );
}

/** One side of the ledger — name + NET per line, with a NET total. */
function LedgerColumn({
  title,
  lines,
  currency,
  onChange,
  pickerDefinitions,
  additionalsByDefId,
  notice,
  billTo,
}: {
  title: string;
  lines: ChargeCostLine[];
  currency: string;
  onChange: (next: ChargeCostLine[]) => void;
  /** Charges side only — definitions offered under + Add. */
  pickerDefinitions?: ChargeCostDefinition[];
  additionalsByDefId?: Map<string, CustomerAdditionalCharge>;
  /** Shown above the lines — the Charges side's rate-card note. */
  notice?: ReactNode;
  /** Charges side of a saved order only. */
  billTo?: BillToProps;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [billToOpen, setBillToOpen] = useState<string | null>(null);
  const payerName = (id: string) => billTo?.payers.find((p) => p.value === id)?.label ?? 'another customer';
  const hasPicker = pickerDefinitions !== undefined;

  const patch = (id: string, next: Partial<ChargeCostLine>) =>
    onChange(lines.map((l) => (l.id === id ? { ...l, ...next } : l)));
  const remove = (id: string) => onChange(lines.filter((l) => l.id !== id));
  const addCustom = () => {
    setMenuOpen(false);
    onChange([...lines, newCustomLine()]);
  };
  const addDefinition = (def: ChargeCostDefinition) => {
    setMenuOpen(false);
    onChange([...lines, buildChargeLine(def, additionalsByDefId?.get(def.id))]);
  };

  return (
    <div className="p-3">
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
        <button
          type="button"
          onClick={() => (hasPicker ? setMenuOpen((o) => !o) : addCustom())}
          className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50"
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>

      {/* Catalogue picker (Charges side). Inline panel so there's no overlay. */}
      {hasPicker && menuOpen && (
        <div className="mb-2 rounded-md border border-slate-200 bg-slate-50 p-2">
          <ul className="max-h-56 space-y-0.5 overflow-auto">
            {pickerDefinitions!.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => addDefinition(d)}
                  className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1 text-left text-xs text-slate-700 hover:bg-purple-50"
                >
                  <span className="truncate">{d.displayName}</span>
                  {additionalsByDefId?.has(d.id) && (
                    <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                      rate
                    </span>
                  )}
                </button>
              </li>
            ))}
            {pickerDefinitions!.length === 0 && (
              <li className="px-2 py-1 text-xs italic text-slate-400">
                No more catalogue charges to add.
              </li>
            )}
          </ul>
          <div className="mt-1 border-t border-slate-200 pt-1">
            <button
              type="button"
              onClick={addCustom}
              className="flex w-full items-center gap-1 rounded-sm px-2 py-1 text-left text-xs font-medium text-purple-600 hover:bg-purple-50"
            >
              <Plus className="h-3.5 w-3.5" />
              Custom charge
            </button>
          </div>
        </div>
      )}

      {notice}

      <ul className="divide-y divide-slate-100">
        {lines.map((l) => {
          const locked = l.isLocked;
          const label = locked && l.rate != null ? `${l.name} ${l.rate}%` : l.name;
          // Only a saved, un-invoiced charge can change payer — a new line has
          // no row to point at until the footer Save.
          const canBillTo = !!billTo && isServerId(l.id) && !l.invoiced;
          const elsewhere = l.billToPartyId ?? null;
          return (
            <li key={l.id} className="group py-1">
            <div className="flex items-center gap-2">
              {/* Catalogue / locked descriptions are read-only; only ad-hoc lines edit. */}
              {l.definitionId || locked ? (
                <span
                  className="min-w-0 flex-1 truncate px-1.5 py-1 text-xs font-medium text-slate-800"
                  title={label}
                >
                  {label}
                </span>
              ) : (
                <input
                  value={l.name}
                  placeholder={`${title.slice(0, -1)} description`}
                  onChange={(e) => patch(l.id, { name: e.target.value })}
                  className="min-w-0 flex-1 rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-xs font-medium text-slate-800 hover:border-slate-200 focus:border-purple-400 focus:bg-white focus:outline-none"
                />
              )}

              {/* Billed elsewhere: a quiet chip, click to change. Otherwise a
                  hover-only icon, so the ledger looks as it always did. */}
              {billTo && elsewhere ? (
                <button
                  type="button"
                  disabled={!canBillTo}
                  onClick={() => setBillToOpen(billToOpen === l.id ? null : l.id)}
                  title={`Billed to ${payerName(elsewhere)}`}
                  className="max-w-[8rem] shrink-0 truncate rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200 hover:bg-sky-100 disabled:cursor-default"
                >
                  → {payerName(elsewhere)}
                </button>
              ) : (
                canBillTo && (
                  <button
                    type="button"
                    onClick={() => setBillToOpen(billToOpen === l.id ? null : l.id)}
                    aria-label="Bill to someone else"
                    title="Bill to someone else…"
                    className={
                      'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm hover:bg-slate-100 ' +
                      (billToOpen === l.id
                        ? 'text-sky-600'
                        : 'text-transparent group-hover:text-slate-400 focus:text-slate-400')
                    }
                  >
                    <UserRoundPen className="h-3.5 w-3.5" />
                  </button>
                )
              )}

              {locked ? (
                <span
                  title="Computed from the rate — not directly editable"
                  className="w-24 shrink-0 px-1.5 py-1 text-right text-xs tabular-nums text-slate-500"
                >
                  {formatAmount(l.net, currency)}
                </span>
              ) : (
                <MoneyInput value={l.net} onChange={(n) => patch(l.id, { net: n })} />
              )}

              {l.isProtected || locked ? (
                <span
                  title={l.isProtected ? "Protected — can't be removed" : 'Computed line'}
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-slate-300"
                >
                  <Lock className="h-3.5 w-3.5" />
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`Remove ${l.name || title.toLowerCase()}`}
                  onClick={() => remove(l.id)}
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-slate-300 hover:bg-red-50 hover:text-red-600 group-hover:text-slate-400"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {billTo && billToOpen === l.id && (
              <div className="mt-1 flex flex-wrap items-center gap-2 rounded-sm bg-sky-50 px-2 py-1 text-[11px] text-slate-600">
                <span>Bill to</span>
                <select
                  value={elsewhere ?? ''}
                  disabled={billTo.pendingId === l.id}
                  onChange={(e) => {
                    billTo.onBillTo(l.id, e.target.value || null);
                    setBillToOpen(null);
                  }}
                  className="min-w-[12rem] rounded-sm border border-slate-300 bg-white px-1.5 py-0.5 text-[11px]"
                >
                  <option value="">
                    {billTo.orderPayerPartyId
                      ? `The order — ${payerName(billTo.orderPayerPartyId)}`
                      : 'The order’s customer'}
                  </option>
                  {billTo.payers
                    .filter((p) => p.value !== billTo.orderPayerPartyId)
                    .map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                </select>
                {billTo.pendingId === l.id && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
                <button
                  type="button"
                  onClick={() => setBillToOpen(null)}
                  aria-label="Close"
                  className="ml-auto rounded-sm p-0.5 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            </li>
          );
        })}

        {lines.length === 0 && (
          <li className="py-6 text-center text-xs italic text-slate-400">
            No {title.toLowerCase()} yet — click{' '}
            <span className="font-medium text-purple-600">+ Add</span> to add a line.
          </li>
        )}
      </ul>

      <div className="mt-2 flex items-center justify-between border-t-2 border-slate-200 pt-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
          Net total
        </span>
        <span className="pr-8 text-sm font-bold tabular-nums text-slate-900">
          {formatAmount(sumNet(lines), currency)}
        </span>
      </div>
    </div>
  );
}

/**
 * Right-aligned money input — always shows 2 decimals. Keeps a local string
 * draft so the operator can type freely; masks out anything that isn't a
 * digit / dot / comma as it's typed, then commits the parsed number on blur
 * and re-formats to `0.00` (commas treated as thousands separators).
 */
function MoneyInput({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [draft, setDraft] = useState(value.toFixed(2));
  useEffect(() => setDraft(value.toFixed(2)), [value]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={(e) => setDraft(e.target.value.replace(/[^\d.,]/g, ''))}
      onFocus={(e) => e.target.select()}
      onBlur={() => {
        const n = parseFloat(draft.replace(/,/g, ''));
        const safe = Number.isFinite(n) ? n : 0;
        setDraft(safe.toFixed(2));
        onChange(safe);
      }}
      className="w-24 shrink-0 rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-right text-xs tabular-nums text-slate-700 hover:border-slate-200 focus:border-purple-400 focus:bg-white focus:outline-none"
    />
  );
}
